import * as React from 'react';

import Form from '@components/library/form/Form';
import InputContainer from '@components/library/input_container/InputContainer';
import { InputType, InputsMap, SelectionValue, ValidationRules } from '@components/library/inputs/Input.props';
import { CustomErrorMapping } from '@components/library/form/Form.props';
import { LabelPosition, StandardSwitch, SwitchSize } from '@components/library/switch';
import { Map } from './FtpIngestTypes';

import './styles/ftp-ingest.scss';

interface CreateFtpIngestFormProps {
  sections: Map;
  collections: Map;
  gettyClient: boolean;
}

const usernameName = 'username';
const passwordName = 'password';
const sectionSelectName = 'section';
const collectionSelectName = 'collection';
const allowFtpId = 'ftp-ingest-allow-ftp';
const usernameTakenErrors = ['file or folder already exists with that name', 'Username has already been taken', 'failed to create folder'];
const badPasswordError = 'Password is among the most common passwords and therefore can not be used';

const mapToOptions = (map: Map): SelectionValue[] => {
  const keys = Object.keys(map);
  return keys.map((key) => ({ value: key, label: map[key] }));
};

const CreateFtpIngestForm = (props: CreateFtpIngestFormProps): JSX.Element => {
  const [error, setError] = React.useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [allowFtp, setAllowFtp] = React.useState(false);
  const [catchAllErrorMessage, setCatchAllErrorMessage] = React.useState(false);
  const sectionOptions = mapToOptions(props.sections);
  const collectionOptions = mapToOptions(props.collections);
  const selectValidationRules: ValidationRules = { isRequired: false };

  const onAsyncCustomValidation = async (inputsMap: InputsMap): Promise<CustomErrorMapping[]> => {
    const { username, password, section, collection } = inputsMap;
    const collectionId = collection && ((collection.value) as SelectionValue).value;
    const sectionId = ((section.value) as SelectionValue).value;
    const body = {
      username: username.value,
      password: password.value,
      section: sectionId,
      collection: collectionId,
      ftp_permission: allowFtp // eslint-disable-line @typescript-eslint/naming-convention
    };
    const url = `/${BFG.resource.slug}/bulk_management/ftp_ingest/create`;
    try {
      const { signal } = new AbortController();
      const options = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${BF_Token}`, // eslint-disable-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json' // eslint-disable-line @typescript-eslint/naming-convention
        },
        signal
      };
      const resp = await fetch(url, { ...options, body: JSON.stringify(body) });
      const json = resp.status === 202 || resp.status === 204 ? {} : await resp.json();
      const errorMapping: CustomErrorMapping[] = [];
      if (json.error) {
        if (usernameTakenErrors.includes(json.error)) {
          const inputName = usernameName;
          const errorString = bfTranslate('This username is already taken');
          errorMapping.push({ inputName, errorString });
        }

        if (json.error === badPasswordError) {
          const inputName = passwordName;
          const errorString = bfTranslate('Please select a less common password');
          errorMapping.push({ inputName, errorString });
        }

        if (![...usernameTakenErrors, badPasswordError].includes(json.error)) {
          setCatchAllErrorMessage(true);
        }

        setError(true);
      } else {
        // Submission successful and will reload page to FTP ingest landing page
        window.location.reload();
      }

      return errorMapping;
    } catch {
      setCatchAllErrorMessage(true);
    }
    return undefined;
  };

  return (
    <div className="ftp-ingest-form">
      {catchAllErrorMessage && (
        <p className="ftp-ingest-error-message">{bfTranslate('There was an error with your FTP creation. Please try again.')}</p>
      )}
      {props.gettyClient && (
        <div className="ftp-ingest-toggle-container">
          <StandardSwitch
            id={allowFtpId}
            isChecked={allowFtp}
            labelPosition={LabelPosition.Right}
            onChange={(): void => setAllowFtp(!allowFtp)}
            size={SwitchSize.Large}
          >
            {`${bfTranslate('Allow insecure')} FTP`}
          </StandardSwitch>
        </div>
      )}
      <Form
        // Page refreshes on successful submission, refresh takes place in onAsyncCustomValidation method
        onAsyncCustomValidation={onAsyncCustomValidation}
        onSubmit={(): void => {}}
        submitButtonText={bfTranslate('Create FTP Ingest')}
      >
        <div className="ftp-ingest-form__input-container">
          <InputContainer
            attributes={{
              autoComplete: 'off',
              name: usernameName,
              type: InputType.Text
            }}
            labelCopy={bfTranslate('FTP name')}
          />
          <div className="ftp-ingest-form__input-container--warning">
            <div className="bff-warning ftp-ingest-form__input-container--warning-icon" />
            <p>{bfTranslate('No special characters allowed.')}</p>
          </div>
        </div>
        <div className="ftp-ingest-form__input-container">
          <InputContainer
            attributes={{
              name: passwordName,
              type: InputType.Password
            }}
            validationRules={{ isRequired: true, minLength: 8 }}
          />
          <div className="ftp-ingest-form__input-container--warning">
            <div className="bff-warning ftp-ingest-form__input-container--warning-icon" />
            <div>
              <p>{bfTranslate('Passwords cannot be recovered or reset.')}</p>
              <p>{bfTranslate('Please make a record of your password.')}</p>
            </div>
          </div>
        </div>
        <InputContainer
          attributes={{ type: InputType.Select, name: sectionSelectName }}
          labelCopy={bfTranslate('Add assets to section')}
          options={sectionOptions}
          selectValue={sectionOptions[0]}
          validationRules={selectValidationRules}
        />
        {collectionOptions.length > 0 ? (
          <InputContainer
            attributes={{ type: InputType.Select, name: collectionSelectName }}
            labelCopy={bfTranslate('Add assets to Collection (Optional)')}
            options={collectionOptions}
            validationRules={selectValidationRules}
          />
        ) : <></>}
      </Form>
    </div>
  );
};

export default CreateFtpIngestForm;
